import React, { useState } from 'react'
import { Form } from 'formik'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom'
import config from '@/config'
import { Button } from '@/components/button'
import { StateDropdown } from '@/components/address-form'
import useBasic from '@/hooks/use-basic'
import useMediaQuery from '@/hooks/use-media-query'
import { getFormattedCountries } from '@/utils/address'
import { breakpoints, fontWeights, colors } from '@/styles'
import { changePhoneNewNumberSilent } from '@/models/requests/ports'
import { AdditionalModalProps } from '@/components/modalize/mui'
import { show as showVerifyPhoneModal } from '@/components/verify-phone-modal'
import {
  Row,
  Group,
  FieldInput,
  FieldDropdown,
  FieldTextarea,
  FieldSwitchToggle,
  PhoneInput
} from '@/components/form'
import { Label } from '@/components/text'
import { FormValues } from './types'

const { WEB_URL } = config

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const PhoneToggleContainer = styled(Group)`
  display: flex;
  align-items: center;
  height: 52px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-top: -8px;
  }
`

const StyledLabel = styled(Label)`
  margin: 12px 0;
`

const ToggleLabel = styled(Label)`
  margin-left: 8px;
  font-weight: ${fontWeights.regular};
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-left: 12px;
    font-size: 14px;
  }
`

const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;

  button:first-child {
    margin-right: 16px;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    button {
      width: 50%;
      margin: 0;
    }
  }
`
const Link = styled.a`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${colors.link};
`
const Warning = styled.span`
  position: absolute;
  bottom: -20px;
  left: 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: ${colors.red64};

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    position: relative;
    bottom: unset;
  }
`

const ButtonWrapper = styled.div`
  position: relative;
`

type SetFieldValue = (field: string, value: any) => void

type FormProps = Pick<AdditionalModalProps, 'ModalFooter'> & {
  data: FormValues
  values: FormValues
  errors: FormValues
  handleSubmit?: () => void
  isSubmitting: boolean
  setFieldValue: SetFieldValue
  close: () => void
}

const LandlordProfileForm: React.FC<FormProps> = ({
  data,
  values,
  errors,
  handleSubmit,
  isSubmitting,
  setFieldValue,
  close,
  ModalFooter
}) => {
  const { t } = useTranslation()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const colSize = displayMobile ? 12 : 6
  const { countries } = useBasic(['countries'])
  const formattedCountries = getFormattedCountries(countries)
  const isPhoneVerified = data.phone_verified_state === 'passed'
  const [verifiedState, setVerifiedState] = useState(isPhoneVerified)
  const history = useHistory()
  const [showVerifyWarning, setShowVerifyWarning] = useState(false)
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  const provinceDropDown = (values: FormValues) => (
    <Group col={colSize}>
      <StateDropdown
        id="company_state"
        name="company_state"
        country={values.company_country_code}
        size="responsive"
        isSearchable={false}
        menuPlacement="top"
      />
    </Group>
  )

  const countryDropDown = (setFieldValue: SetFieldValue) => (
    <Group col={colSize}>
      <FieldDropdown
        id="company_country_code"
        name="company_country_code"
        options={formattedCountries}
        placeholder={t('r.profile.edit.country.label', 'Country')}
        onChange={() => setFieldValue('company_state', '')}
        size="responsive"
        menuPlacement="top"
      />
    </Group>
  )

  const companyZip = () => (
    <Group col={colSize}>
      <FieldInput
        name="company_zip"
        placeholder={t('l.profile.edit.postal_code.label', 'Postal code')}
        size="responsive"
      />
    </Group>
  )

  const companyCity = () => (
    <Group col={colSize}>
      <FieldInput
        name="company_city"
        placeholder={t('l.profile.edit.city.label', 'City')}
        size="responsive"
      />
    </Group>
  )

  const companyAddress = () => (
    <Group col={colSize}>
      <FieldInput
        name="company_address"
        placeholder={t('l.profile.edit.street_address.label', 'Street address')}
        size="responsive"
      />
    </Group>
  )

  const companyUnitNumber = () => (
    <Group col={colSize}>
      <FieldInput
        name="company_unit_number"
        placeholder={t('l.profile.edit.unit_number.label', 'Unit number')}
        size="responsive"
      />
    </Group>
  )

  const renderAddressSection = (setFieldValue: SetFieldValue, values: FormValues) =>
    data.account_type === 'business' ? (
      <>
        <StyledLabel fontSize="14px">
          {t('l.profile.edit.company_address.text', 'Company Information')}
        </StyledLabel>
        <Row>
          <Group col={colSize}>
            <FieldInput
              name="company_name"
              placeholder={t('l.profile.edit.company_name.label', 'Company name')}
              size="responsive"
            />
          </Group>
          <Group col={colSize}>
            <PhoneInput
              name="company_phone_number"
              countryCodeAlpha={data.company_phone_country_code_alpha}
              placeholder={t('l.profile.edit.company_phone_number.label', 'Phone number')}
              onCountryCodeChange={code => {
                setFieldValue('company_phone_country_code', code.calling_code)
                setFieldValue('company_phone_country_code_alpha', code.txt_id)
              }}
              size="responsive"
            />
          </Group>
        </Row>
        <Row>
          <Group col={colSize}>
            <FieldInput
              name="company_email"
              placeholder={t('l.profile.edit.company_email.label', 'Email')}
              size="responsive"
            />
          </Group>
          {companyAddress()}
        </Row>
        <Row>
          {companyUnitNumber()}
          {companyCity()}
        </Row>
        <Row>
          {countryDropDown(setFieldValue)}
          {provinceDropDown(values)}
        </Row>
        <Row>{companyZip()}</Row>
      </>
    ) : (
      <>
        <StyledLabel fontSize="14px">
          {t('l.profile.edit.current_address.text', 'Enter Your Current Address')}
        </StyledLabel>
        <Row>
          {companyUnitNumber()}
          {companyAddress()}
        </Row>
        <Row>
          {companyCity()}
          {companyZip()}
        </Row>
        <Row>
          {countryDropDown(setFieldValue)}
          {provinceDropDown(values)}
        </Row>
      </>
    )

  const handleVerifiedState = () => setVerifiedState(true)
  const sendCodeToPhone = async () => {
    const result = await changePhoneNewNumberSilent({
      phone: values.phone
    })
    if (result?.response.ok) {
      showVerifyPhoneModal({
        initialStep: 'code',
        phone: values.phone,
        altBackButton: true,
        customFunc: handleVerifiedState,
        history
      })
    }
  }

  return (
    <StyledForm>
      <StyledLabel fontSize="14px">
        {t('l.profile.edit.personal_information.subtitle', 'Personal Information')}
      </StyledLabel>
      <Row>
        <Group col={colSize}>
          <FieldInput
            name="first_name"
            disabled={data.identity_verified_state === 'verified'}
            placeholder={t(
              'l.profile.edit.legal_first_and_middle_name.label',
              'Legal first & middle name'
            )}
            required
            size="responsive"
          />
        </Group>
        <Group col={colSize}>
          <FieldInput
            name="last_name"
            disabled={data.identity_verified_state === 'verified'}
            placeholder={t('l.profile.edit.legal_last_name.label', 'Legal last name')}
            required
            size="responsive"
          />
        </Group>
      </Row>
      <Row>
        <Group col={colSize}>
          <FieldInput
            name="preferred_name"
            placeholder={t('l.profile.edit.preferred_name.label', 'Preferred name')}
            size="responsive"
          />
        </Group>
        <Group col={colSize}>
          <FieldInput
            name="username"
            placeholder={t('l.profile.edit.email.label', 'Email')}
            required
            size="responsive"
          />
        </Group>
      </Row>
      <Row>
        <Group col={colSize} bottom={!isMobile ? 0 : undefined}>
          <PhoneInput
            name="phone"
            disabled={verifiedState}
            value={values.phone_number}
            countryCodeAlpha={data.phone_company_country_code_alpha}
            placeholder={t('l.profile.edit.phone_number.label', 'Phone number')}
            required
            size="responsive"
          />
          {verifiedState && (
            <Link
              href={`${WEB_URL}/app/settings/change-phone-number`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('l.profile.change.phone_number.label', 'Change phone number')}
            </Link>
          )}
        </Group>
        <Group col={colSize}>
          <ButtonWrapper>
            <Button
              theme="primary"
              size="small"
              onClick={sendCodeToPhone}
              minHeight="48px"
              minWidth="186px"
              width={isMobile ? '100%' : undefined}
              disabled={!!errors.phone || !values.phone || verifiedState}
            >
              {verifiedState
                ? t('common.verified', 'Verified')
                : t('l.profile.verify.phone_number.label', 'Verify Phone Nymber')}
            </Button>
            {showVerifyWarning && (
              <Warning>{t('l.profile.verify.phone_number.warning', 'Required')}</Warning>
            )}
          </ButtonWrapper>
        </Group>
      </Row>
      <Row>
        <PhoneToggleContainer col={colSize}>
          <FieldSwitchToggle name="hide_phone_number" id="hide_phone_number" />
          <ToggleLabel fontSize="12px" htmlFor="hide_phone_number">
            {t('l.profile.edit.hide_phone_number.label', 'Hide phone number from public')}
          </ToggleLabel>
        </PhoneToggleContainer>
      </Row>
      <StyledLabel fontSize="14px">
        {t('l.profile.edit.my_story.subtitle', 'Enter Your Story')}
      </StyledLabel>
      <Row>
        <Group col={12}>
          <FieldTextarea
            name="about_me"
            maxLength={260}
            height={120}
            fontSize={displayMobile ? 16 : 14}
            placeholder={t(
              'l.profile.edit.my_story.placeholder',
              'Tell us a little about yourself...'
            )}
          />
        </Group>
      </Row>
      {data.account_type === 'business' && (
        <>
          <StyledLabel fontSize="14px">
            {t('l.profile.edit.work_history.subtitle', 'Enter Your Work History')}
          </StyledLabel>
          <Row>
            <Group col={colSize}>
              <FieldInput
                name="work_history_year_started"
                placeholder={t('l.profile.edit.year_started.label', 'Year started')}
                size="responsive"
              />
            </Group>
            <Group col={colSize}>
              <FieldDropdown
                id="work_history_properties_managed"
                name="work_history_properties_managed"
                placeholder={t('l.profile.edit.properties_managed.label', 'Properties managed')}
                options={[
                  { value: '0-9', label: '0-9' },
                  { value: '10-49', label: '10-49' },
                  { value: '50-99', label: '50-99' },
                  { value: '100+', label: '100+' }
                ]}
                size="responsive"
              />
            </Group>
          </Row>
        </>
      )}
      {data.user_role === 'administrator' && renderAddressSection(setFieldValue, values)}
      <ModalFooter>
        <ButtonsWrapper>
          <Button theme="secondary" size={displayMobile ? 'medium' : 'small'} onClick={close}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            theme="primary"
            size={displayMobile ? 'medium' : 'small'}
            onClick={verifiedState ? handleSubmit : () => setShowVerifyWarning(true)}
            disabled={isSubmitting}
          >
            {t('l.plan_settings.edit.save_changes.button', 'Save Changes')}
          </Button>
        </ButtonsWrapper>
      </ModalFooter>
    </StyledForm>
  )
}

export default LandlordProfileForm
